import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Menu, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { EnhancedSidebar } from '@/components/common/EnhancedSidebar';
import { useIsAuthenticated } from '@/hooks/redux';
import { cn, formatNumberWithCommas, showToastMessage } from '@/utils';
import { RootState } from '@/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  useAvailbleRolesQuery,
  useGetUserQuery,
  useGetWalletQuery,
  useGetMfaStatusQuery,
  useLogoutMutation,
} from '@/store/api/authApi';
import { clearUser } from '@/store/slices/userSlice';
import { logout as clearAuth } from '@/store';
import apiSlice from '@/store/api/apiSlice';
import { STATUS_LOGOUT_MESSAGES } from '@/utils/constent';
import { Modal } from '@/components/common/Modal';
import { MfaSettingsModal } from '@/features/mfa/MfaSettingsModal';
import { ChangePassword } from '@/components/common/ChangePassword';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { NotificationSidebar } from '@/components/common/NotificationSidebar';
import { NewNotificationBanner } from '@/components/common/NewNotificationBanner';
import { useGetUnreadNotificationsQuery } from '@/store/api/notificationApi';

function playAlertSound() {
  const audio = new Audio('/images/notification.mp3');
  audio.play().catch(() => {
    // Browser may block autoplay
  });
}

export function DashboardLayout() {
  const [logout] = useLogoutMutation();
  const BLOCKED_STATUSES = ['inactive', 'suspended'];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ismobile = window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = React.useState(!ismobile);
  const [notifOpen, setNotifOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [isBellRinging, setIsBellRinging] = useState(false);
  const prevUnreadCountRef = useRef<number | null>(null);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaAction, setMfaAction] = useState<'enable' | 'disable'>('enable');
  const isAuthenticated = useIsAuthenticated();
  const {
    data: mfaStatus,
    isFetching: isFetchingMfaStatus,
    refetch: refetchMfaStatus,
  } = useGetMfaStatusQuery(undefined, { skip: !isAuthenticated });
  const { data: unreadData } = useGetUnreadNotificationsQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 10000,
  });
  const unreadNotifications = unreadData?.notifications ?? [];
  const unreadCount = unreadNotifications.length;
  const latestNotifTitle = unreadNotifications[0]?.notification ?? '';

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Ignore first load
    if (prevUnreadCountRef.current === null) {
      prevUnreadCountRef.current = unreadCount;
      return;
    }

    // New notification received
    if (unreadCount > prevUnreadCountRef.current) {
      playAlertSound();

      setIsBellRinging(true);
      setBannerVisible(true);

      timer = setTimeout(() => {
        setIsBellRinging(false);
      }, 3000);
    }

    prevUnreadCountRef.current = unreadCount;

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [unreadCount]);

  const { data: userData } = useGetUserQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 20000,
  });

  useAvailbleRolesQuery(undefined, { skip: !isAuthenticated });
  const { data } = useGetWalletQuery(undefined, { skip: !isAuthenticated, pollingInterval: 20000 });
  const user = useSelector((state: RootState) => state.user);
  const [userClosed, setUserClosed] = React.useState(false);
  const closeModal = () => setUserClosed(true);
  const { isOwner, isRoleUser } = useRoleCheck();

  const mainAmount = useMemo(() => {
    return data?.find((w) => w.type === 'Main')?.amount ?? '0';
  }, [data]);
  const showModal = Boolean(user.data && user.data.passwordChanged === false && !userClosed);

  useEffect(() => {
    if (!userData?.status) return;

    if (BLOCKED_STATUSES.includes(userData.status.toLowerCase())) {
      // 🔹 Clear auth (example)
      const message =
        STATUS_LOGOUT_MESSAGES[userData?.status.toLowerCase()] ??
        'Your session has ended. Please log in again.';

      handleLogout(message);
      navigate('/login', { replace: true });
    }
  }, [userData?.status, navigate]);
  const handleLogout = async (message: string) => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      apiSlice.util.resetApiState();

      showToastMessage('error', message);
      window.location.href = '/login';
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };
  const displayName = isRoleUser ? user?.data?.email : isOwner ? 'OWNER' : user?.data?.username;

  const finishMfaSetup = async () => {
    try {
      await logout().unwrap();
    } catch {
      // The local session still needs to end after MFA setup, even if logout is already invalid.
    } finally {
      dispatch(clearAuth());
      dispatch(clearUser());
      dispatch(apiSlice.util.resetApiState());
      setMfaModalOpen(false);
      navigate('/login', { replace: true });
    }
  };

  const finishMfaDisable = () => {
    refetchMfaStatus();
    setMfaModalOpen(false);
  };

  const openMfaModal = (action: 'enable' | 'disable') => {
    setMfaAction(action);
    setMfaModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background ">
      {/* Enhanced Sidebar */}
      <EnhancedSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      {/* Main Content */}
      <div className={cn('transition-all duration-300', sidebarOpen ? 'lg:ml-72' : 'lg:ml-16')}>
        {/* Top Navigation */}
        <header className="sticky top-0 z-40 border-b border-[#EAAD07]/20 bg-white shadow-sm">
          <div className="flex h-16 items-center px-4 bg-white">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[#072C50] hover:bg-[#072C50]/5"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Notification Bell */}
            <button
              onClick={() => setNotifOpen(true)}
              className="relative ml-auto mr-3 text-[#072C50] hover:text-[#EAAD07] transition-colors"
              aria-label="Notifications"
            >
              <Bell className={cn('h-6 w-6', isBellRinging && 'animate-bell')} />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#EAAD07] text-[10px] font-bold text-[#072C50] leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {isOwner && (
              <div className="mr-4 flex items-center gap-3 text-[#072C50]">
                <span className="max-md:hidden text-sm font-medium">
                  Multi-Factor Authentication
                </span>
                <span className="md:hidden text-sm font-medium">MFA</span>
                <Switch
                  aria-label="Toggle MFA"
                  className="data-[state=checked]:bg-emerald-700 data-[state=unchecked]:bg-slate-500 [&>span]:!bg-white"
                  checked={Boolean(mfaStatus?.isMfaEnabled)}
                  disabled={isFetchingMfaStatus}
                  onCheckedChange={(checked) => openMfaModal(checked ? 'enable' : 'disable')}
                />
              </div>
            )}

            <div className="border border-[#EAAD07]/40 rounded-lg flex-end gap-2 px-4 bg-[#F5F7FA]">
              <img src="/images/profile.png" alt="" className="h-10 w-10" />
              <div className="flex flex-col justify-start items-start py-1 font-semibold text-[#072C50]">
                <span className="leading-4">{displayName ?? 'OWNER'}</span>
                <span className="flex items-center text-[#EAAD07]">
                  {formatNumberWithCommas(mainAmount)}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 py-2 sm:py-6 bg-main-bg min-h-[calc(100dvh-65px)]">
          <Outlet />
        </main>
      </div>
      {showModal && (
        <Modal
          open={showModal}
          style="top-[25%] left-1/2 -translate-x-1/2 border-purple rounded-2xl shadow-sm"
          bgBlur={true}
        >
          <ChangePassword
            close={closeModal}
            title={true}
            inputClass="border-purple "
            lableClass=""
          />
        </Modal>
      )}

      <NotificationSidebar open={notifOpen} onClose={() => setNotifOpen(false)} />

      <NewNotificationBanner
        visible={bannerVisible}
        title={latestNotifTitle}
        onOpen={() => {
          setBannerVisible(false);
          setNotifOpen(true);
        }}
        onDismiss={() => setBannerVisible(false)}
      />
      <MfaSettingsModal
        open={mfaModalOpen}
        action={mfaAction}
        activeMethod={mfaStatus?.activeMethod ?? null}
        defaultUsername={user?.data?.username}
        onOpenChange={setMfaModalOpen}
        onConfigured={finishMfaSetup}
        onDisabled={finishMfaDisable}
      />
    </div>
  );
}
